import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Sparkles, X } from 'lucide-react';
import EmpreendimentoFormBasico from './EmpreendimentoFormBasico';
import EmpreendimentoFormEspecifico from './EmpreendimentoFormEspecifico';
import ImportarEmpreendimentoIA from './ImportarEmpreendimentoIA';
import { EmpreendimentoCreate } from '../../types/empreendimento';
import { empreendimentoService } from '../../services/empreendimentoService';
import { authService } from '../../services/auth';
import { tokenService } from '../../services/tokenService';
import { rascunhoEmpreendimentoService } from '../../services/rascunhoEmpreendimentoService';
import { ArquivosNomes } from '../../types/rascunhoEmpreendimento';

// Nome preenchido = considerado "conteúdo relevante" pro rascunho existir.
// Sem essa checagem, todo mundo que só abre a página e sai geraria um
// rascunho vazio na lista.
const temConteudoRelevante = (basicData: EmpreendimentoCreate | null, specificDataLive: any): boolean => {
  if (basicData?.nome && basicData.nome.trim()) return true;
  const torres = specificDataLive?.torres;
  const avulsas = specificDataLive?.unidades_sem_torre;
  return (Array.isArray(torres) && torres.some((t: any) => t.unidades?.some((andar: any[]) => andar.length > 0))) ||
    (Array.isArray(avulsas) && avulsas.length > 0);
};

// Debounce do autosave em localStorage — evita serializar torres/unidades
// inteiras a cada tecla digitada.
const AUTOSAVE_DEBOUNCE_MS = 800;

// As 3 etapas reais do cadastro. "Informações Específicas" sempre foi na
// verdade 2 sub-passos escondidos dentro de EmpreendimentoFormEspecifico
// (Fase/Tipo/Unidades e depois Imagens/Documentos) — a barra só mostrava 2
// círculos porque esse sub-passo nunca era reportado pro wizard pai.
const WIZARD_STEPS: { n: 1 | 2 | 3; label: string }[] = [
  { n: 1, label: 'Informações Básicas' },
  { n: 2, label: 'Unidades e Configurações' },
  { n: 3, label: 'Imagens e Documentos' },
];

// Mapeia os campos que a API pode devolver em erros de validação (400) pra
// etapa da barra correspondente — usado pra destacar em vermelho qual etapa
// causou o erro no envio final, já que a API retorna só nome de campo, sem
// contexto de "etapa do wizard".
const FIELD_TO_WIZARD_STEP: Record<string, 1 | 2 | 3> = {
  nome: 1, endereco: 1, area_total: 1, area_construida: 1, incorporadora: 1,
  elevadores: 1, vagas: 1, vagas_rotativas: 1, is_lancamento: 1,
  data_entrega: 1, data_inicio_comercializacao: 1, observacao: 1, heading: 1, pitch: 1,
  fase_empreendimento: 2, tipo_empreendimento: 2, item_empreendimento: 2, partes_empreendimento: 2,
  imagens: 3, tabela_preco: 3, book: 3, arquivo_extra_1: 3, arquivo_extra_2: 3,
};

// Stub usado pra EmpreendimentoFormEspecifico nunca ficar sem `basicData` —
// agora dá pra pular pra etapa 2/3 antes mesmo de preencher a etapa 1, então
// não podemos mais depender de "basicData só existe depois que a etapa 1 foi
// confirmada". O valor de verdade é sempre `basicData` (sincronizado a cada
// digitação via onDataChange); isso aqui só evita undefined antes da primeira
// digitação.
const EMPTY_BASIC_DATA: EmpreendimentoCreate = {
  vagas_rotativas: 0,
  vagas: 0,
  endereco: 0,
  tabela_preco: '',
  area_total: 0,
  area_construida: 0,
  is_lancamento: false,
  nome: '',
  incorporadora: 0,
  elevadores: 0,
};

const NovoEmpreendimento: React.FC = () => {
  // Etapa da barra de progresso que está visível no momento — única fonte de
  // verdade pra qual dos 3 "sub-formulários" o usuário está vendo.
  const [activeStep, setActiveStep] = useState<1 | 2 | 3>(1);

  // Sempre a versão mais atual dos dados básicos, atualizada a cada
  // digitação (via onDataChange de EmpreendimentoFormBasico) — não depende
  // mais de clicar "Próximo" pra existir, porque agora dá pra sair da etapa 1
  // sem confirmá-la.
  const [basicData, setBasicData] = useState<EmpreendimentoCreate | null>(null);
  const [basicDataValid, setBasicDataValid] = useState(false);
  // Validade "ao vivo" das etapas 2 e 3, reportada por EmpreendimentoFormEspecifico.
  const [specificoValidity, setSpecificoValidity] = useState<{ step2: boolean; step3: boolean }>({ step2: false, step3: false });

  // Seed usado só pra popular o formulário inicialmente (import por IA) — via
  // prop `initialData`. Não é atualizado a cada digitação (isso é `basicData`
  // acima) pra não brigar com o que o usuário está digitando.
  const [basicDataSeed, setBasicDataSeed] = useState<EmpreendimentoCreate | undefined>(undefined);
  const [specificData, setSpecificData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Comando de navegação pro sub-passo interno de EmpreendimentoFormEspecifico,
  // disparado por clique na barra. `token` sempre incrementa pra forçar o
  // efeito do filho a rodar mesmo pedindo o mesmo step de novo (ver comentário
  // na prop `requestedStep` de EmpreendimentoFormEspecifico).
  const [requestedSpecificoStep, setRequestedSpecificoStep] = useState<{ step: 1 | 2; token: number } | undefined>(undefined);
  const requestSpecificoStep = (target: 1 | 2) => {
    setRequestedSpecificoStep(prev => ({ step: target, token: (prev?.token ?? 0) + 1 }));
  };
  // Etapas destacadas em vermelho por causa de um erro de validação/API no
  // último envio — some assim que o usuário corrige e reenvia.
  const [errorSteps, setErrorSteps] = useState<Set<1 | 2 | 3>>(new Set());
  const navigate = useNavigate();

  // Botão "Preencher com IA" só faz sentido na etapa 1 — não é uma etapa do
  // wizard, é opcional.
  const [showImportModal, setShowImportModal] = useState(false);

  // Preenchidos pela importação por IA (texto livre, resolvido contra o banco
  // só quando EmpreendimentoFormBasico monta — ver initialEnderecoTexto /
  // initialIncorporadoraTexto nesse componente).
  const [enderecoTexto, setEnderecoTexto] = useState<{ uf?: string; cidade?: string; bairro?: string }>();
  const [incorporadoraTexto, setIncorporadoraTexto] = useState<string>();

  // --- Rascunho local (persistência em localStorage) ---
  const [searchParams, setSearchParams] = useSearchParams();
  const [draftId, setDraftId] = useState<string | null>(null);
  // "Ao vivo": atualizado a cada mudança em EmpreendimentoFormEspecifico via
  // onDataChange — NUNCA é devolvido como initialSpecificData (ver comentário
  // na prop onDataChange desse componente). Só serve pro autosave.
  const [specificDataLive, setSpecificDataLive] = useState<any>(null);
  const [arquivosNomesLive, setArquivosNomesLive] = useState<ArquivosNomes>({});
  // criadoEm do rascunho tem que ficar estável entre saves — não dá pra
  // recalcular a cada autosave (isso reescreveria a data de criação toda vez).
  const draftCriadoEmRef = useRef<string | null>(null);

  useEffect(() => {
    // Verificar se o usuário está autenticado
    if (!authService.isAuthenticated()) {
      navigate('/login');
    }
  }, [navigate]);

  // Carrega um rascunho salvo quando a página abre com ?rascunho=<id> (link
  // "Continuar preenchimento" da listagem de empreendimentos). Só no mount —
  // esse fluxo não precisa reagir a mudanças posteriores de searchParams.
  useEffect(() => {
    const idParam = searchParams.get('rascunho');
    if (!idParam) return;
    const rascunho = rascunhoEmpreendimentoService.obter(idParam);
    if (!rascunho) {
      toast.warn('Rascunho não encontrado — pode ter expirado ou sido removido.');
      return;
    }
    draftCriadoEmRef.current = rascunho.criadoEm;
    setDraftId(idParam);
    setBasicDataSeed(rascunho.basicData as EmpreendimentoCreate);
    setSpecificData(rascunho.specificData);
    const nomesArquivos = Object.values(rascunho.arquivosNomes || {}).filter(Boolean);
    if (nomesArquivos.length > 0) {
      toast.info(
        `Este rascunho tinha arquivo(s) anexado(s) (${nomesArquivos.join(', ')}) — como arquivos não ficam salvos localmente, anexe de novo se quiser reenviá-los.`,
        { autoClose: 10000 }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autosave: gera um id de rascunho na primeira vez que existe conteúdo
  // relevante (e grava isso na URL, pra sobreviver a um refresh), e depois
  // salva com debounce a cada mudança em basicData/specificDataLive.
  useEffect(() => {
    if (!temConteudoRelevante(basicData, specificDataLive)) return;

    let idAtual = draftId;
    if (!idAtual) {
      idAtual = rascunhoEmpreendimentoService.gerarId();
      draftCriadoEmRef.current = new Date().toISOString();
      setDraftId(idAtual);
      const novosParams = new URLSearchParams(searchParams);
      novosParams.set('rascunho', idAtual);
      setSearchParams(novosParams, { replace: true });
    }

    const handle = setTimeout(() => {
      const agora = new Date().toISOString();
      rascunhoEmpreendimentoService.salvar({
        id: idAtual as string,
        nome: basicData?.nome || '',
        criadoEm: draftCriadoEmRef.current || agora,
        atualizadoEm: agora,
        basicData: basicData || {},
        specificData: specificDataLive,
        arquivosNomes: arquivosNomesLive,
      });
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicData, specificDataLive, arquivosNomesLive, draftId]);

  const handleDescartarRascunho = () => {
    if (!draftId) return;
    if (!window.confirm('Descartar este rascunho? Os dados preenchidos aqui serão perdidos.')) return;
    rascunhoEmpreendimentoService.remover(draftId);
    // Reload de propósito: os dois formulários filhos ficam sempre montados
    // e não têm um jeito limpo de "esvaziar" o próprio estado interno a
    // partir daqui — recarregar a página é a forma simples e garantida de
    // voltar pra um formulário em branco.
    window.location.href = '/empreendimentos/novo';
  };

  const handleIaConcluir = (dados: {
    basicData: Record<string, any>;
    specificData: any;
    enderecoTexto: { uf?: string; cidade?: string; bairro?: string };
    incorporadoraTexto: string;
  }) => {
    setBasicDataSeed(dados.basicData as EmpreendimentoCreate);
    setSpecificData(dados.specificData);
    setEnderecoTexto(dados.enderecoTexto);
    setIncorporadoraTexto(dados.incorporadoraTexto);
    setShowImportModal(false);
  };

  // "Próximo" na etapa 1: os dados já estão sincronizados via onDataChange —
  // isso só avança a tela e garante que a etapa 2 abre no sub-passo de
  // Unidades (não fica presa em Imagens caso o usuário tenha navegado lá antes).
  const handleBasicSubmit = () => {
    setActiveStep(2);
    requestSpecificoStep(1);
  };

  const handleBack = () => {
    setActiveStep(1);
  };

  // Clique num círculo da barra de progresso: navegação livre, mesmo que a
  // etapa atual não esteja preenchida — os dois formulários ficam sempre
  // montados (só a visibilidade muda), então nenhum dado digitado se perde.
  const handleStepClick = (target: 1 | 2 | 3) => {
    if (target === 1) {
      setActiveStep(1);
      return;
    }
    setActiveStep(target);
    requestSpecificoStep(target === 2 ? 1 : 2);
  };

  // Espelha o sub-passo interno de EmpreendimentoFormEspecifico quando o
  // usuário navega pelos próprios botões "Próxima Etapa"/"Voltar" do
  // formulário (não pela barra). Como o componente agora fica sempre
  // montado (até enquanto escondido, na etapa 1), esse callback dispara
  // mesmo sem o usuário ter saído da etapa 1 (o próprio mount já reporta o
  // step interno inicial) — sem o guard abaixo, isso jogava o wizard direto
  // pra etapa 2 ao abrir a página. Só deixamos ele mandar depois que o
  // usuário já saiu da etapa 1 por conta própria (clique na barra ou
  // "Próximo" do formulário básico).
  const handleSpecificoStepChange = (internalStep: 1 | 2) => {
    setActiveStep(prev => (prev === 1 ? prev : (internalStep === 1 ? 2 : 3)));
  };

  const handleSpecificSubmit = async (formattedData: any, rawData: any) => {
    // Preservar dados específicos para navegação
    setSpecificData(rawData);
    setError(null);
    setErrorSteps(new Set());

    try {
      // Combinar dados básicos e específicos
      const completeData = {
        ...(basicData ?? EMPTY_BASIC_DATA),
        ...formattedData
      };

      // Extrair partes do empreendimento dos dados específicos
      const partes = formattedData.partes_empreendimento || [];

      // Usar o novo método que cria empreendimento e partes em sequência
      const empreendimento = await empreendimentoService.createWithParts(completeData, partes);

      // Backend confirmou a criação — o rascunho local não faz mais sentido.
      if (draftId) {
        rascunhoEmpreendimentoService.remover(draftId);
      }

      navigate('/empreendimentos');

    } catch (err: any) {
      console.error('Erro detalhado ao salvar empreendimento:', err);

      let errorMessage = 'Erro desconhecido ao salvar empreendimento';

      if (err.response) {
        // Erro da API
        const { status, data } = err.response;
        console.error('Status da API:', status, 'Dados:', data);

        if (status === 400) {
          if (data && typeof data === 'object') {
            // Erros de validação específicos
            const fieldErrors: string[] = [];
            const stepsWithErrors = new Set<1 | 2 | 3>();
            Object.entries(data).forEach(([field, messages]) => {
              const mappedStep = FIELD_TO_WIZARD_STEP[field];
              if (mappedStep) stepsWithErrors.add(mappedStep);
              if (Array.isArray(messages)) {
                fieldErrors.push(`${field}: ${messages.join(', ')}`);
              } else if (typeof messages === 'string') {
                fieldErrors.push(`${field}: ${messages}`);
              }
            });
            setErrorSteps(stepsWithErrors);
            // Leva o usuário direto pra primeira etapa com erro, em vez de
            // deixar ele só olhando pro texto sem saber onde corrigir.
            if (stepsWithErrors.size > 0) {
              const firstErrorStep = Math.min(...Array.from(stepsWithErrors)) as 1 | 2 | 3;
              if (firstErrorStep === 1) {
                setActiveStep(1);
              } else {
                setActiveStep(firstErrorStep);
                requestSpecificoStep(firstErrorStep === 2 ? 1 : 2);
              }
            }
            errorMessage = `Erros de validação:\n${fieldErrors.join('\n')}`;
          } else if (typeof data === 'string') {
            errorMessage = data;
          }
        } else if (status === 401) {
          errorMessage = 'Sessão expirada. Faça login novamente.';
        } else if (status === 403) {
          errorMessage = 'Sem permissão para realizar esta ação.';
        } else if (status === 404) {
          errorMessage = 'Recurso não encontrado.';
        } else if (status === 500) {
          errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
        } else {
          errorMessage = `Erro do servidor (${status}): ${data || 'Erro desconhecido'}`;
        }
      } else if (err.request) {
        // Erro de rede
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      } else if (err.message) {
        // Erro JavaScript
        errorMessage = err.message;
      }

      setError(errorMessage);
    }
  };

  // Validade de cada etapa pra colorir a barra: vermelho enquanto incompleta,
  // verde quando ok. Um erro do último envio (errorSteps) sempre força
  // vermelho, mesmo que a etapa pareça válida no cliente (o backend rejeitou
  // por um motivo que a validação local não cobre).
  const stepValid: Record<1 | 2 | 3, boolean> = {
    1: basicDataValid,
    2: specificoValidity.step2,
    3: specificoValidity.step3,
  };
  const allStepsValid = stepValid[1] && stepValid[2] && stepValid[3];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="py-6">
        <div className="mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-[#29306a]">Novo Empreendimento</h1>
              <p className="mt-2 text-sm text-[#64748b]">
                Cadastre um novo empreendimento no sistema
              </p>
            </div>
            <div className="flex-shrink-0 flex items-center gap-3">
              {draftId && (
                <span className="text-xs text-gray-400" title="Salvo automaticamente no seu navegador enquanto você preenche">
                  Rascunho salvo
                </span>
              )}
              {draftId && (
                <button
                  type="button"
                  onClick={handleDescartarRascunho}
                  className="text-sm text-gray-500 hover:text-red-600 underline"
                >
                  Descartar rascunho
                </button>
              )}
              {activeStep === 1 && (
                <button
                  type="button"
                  onClick={() => setShowImportModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
                  title="Lê book e/ou tabela de preço e pré-preenche o formulário pra você revisar"
                >
                  <Sparkles size={16} />
                  Preencher com IA
                </button>
              )}
            </div>
          </div>
          <div className="mt-4 flex items-center space-x-4">
            {WIZARD_STEPS.map((s, idx) => {
              const hasError = errorSteps.has(s.n);
              const isValid = stepValid[s.n] && !hasError;
              const isActive = activeStep === s.n;
              return (
                <React.Fragment key={s.n}>
                  <button
                    type="button"
                    onClick={() => handleStepClick(s.n)}
                    title={!isValid ? 'Etapa incompleta — clique pra ver o que falta' : undefined}
                    className={`flex items-center cursor-pointer ${
                      hasError ? 'text-red-500' : isValid ? 'text-emerald-600' : 'text-red-400'
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                        hasError
                          ? 'border-red-500 bg-red-500 text-white'
                          : isValid
                          ? 'border-emerald-500 bg-emerald-500 text-white'
                          : 'border-red-400 bg-red-50 text-red-500'
                      } ${isActive ? 'ring-2 ring-offset-2 ring-[#32bef0]' : ''}`}
                    >
                      {s.n}
                    </div>
                    <span className="ml-2 text-sm font-medium">{s.label}</span>
                  </button>
                  {idx < WIZARD_STEPS.length - 1 && <div className="flex-1 h-px bg-gray-300"></div>}
                </React.Fragment>
              );
            })}
          </div>
          {!allStepsValid && (
            <p className="mt-2 text-xs text-gray-500">
              Navegue livremente entre as etapas — o envio final só é liberado quando todas estiverem verdes.
            </p>
          )}
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            <div className="flex justify-between items-start">
              <div className="flex-1 whitespace-pre-line">
                {error}
              </div>
              <button
                onClick={() => {
                  setError(null);
                  setErrorSteps(new Set());
                }}
                className="ml-4 text-red-400 hover:text-red-600"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Os dois formulários ficam sempre montados — só a visibilidade muda.
            Isso é o que permite pular entre etapas sem perder o que já foi
            digitado (antes, trocar de etapa desmontava o formulário anterior). */}
        <div className={activeStep === 1 ? '' : 'hidden'}>
          <EmpreendimentoFormBasico
            onNext={handleBasicSubmit}
            onDataChange={(data, isValid) => {
              setBasicData(data);
              setBasicDataValid(isValid);
            }}
            initialData={basicDataSeed}
            initialEnderecoTexto={enderecoTexto}
            initialIncorporadoraTexto={incorporadoraTexto}
          />
        </div>
        <div className={activeStep === 1 ? 'hidden' : ''}>
          <EmpreendimentoFormEspecifico
            basicData={basicData ?? EMPTY_BASIC_DATA}
            basicDataValid={basicDataValid}
            onSave={handleSpecificSubmit}
            onBack={handleBack}
            initialSpecificData={specificData}
            onStepChange={handleSpecificoStepChange}
            requestedStep={requestedSpecificoStep}
            onValidationError={() => setErrorSteps(new Set<1 | 2 | 3>([2]))}
            onValidityChange={(step2, step3) => setSpecificoValidity({ step2, step3 })}
            onDataChange={(rawData, arquivosNomes) => {
              setSpecificDataLive(rawData);
              setArquivosNomesLive(arquivosNomes);
            }}
          />
        </div>
      </div>

      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setShowImportModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 z-10"
              title="Fechar"
            >
              <X size={22} />
            </button>
            <div className="p-6">
              <ImportarEmpreendimentoIA
                onConcluir={handleIaConcluir}
                onPular={() => setShowImportModal(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NovoEmpreendimento;
